import React from "react";
import { useModalDialog } from "../../../hooks/useModalDialog";
import { ShortcutKeys } from "../../atoms/ShortcutKeys";
import { SoonBadge } from "../../atoms/SoonBadge";
import { Switch } from "../../atoms/Switch";
import { DialogShell } from "../../molecules/DialogShell";
import {
  Empty,
  PairDot,
  Row,
  RowKeys,
  RowLabel,
  Rows,
  Section,
  SectionTitle,
  Sections,
  Setting,
  SettingDescription,
  SettingLabel,
  SettingText,
} from "./ShortcutsDialog.styles";
import type { ShortcutsDialogProps } from "./ShortcutsDialog.types";

/** A reference sheet of keyboard shortcuts, grouped. Every chord of a row is shown. */
export const ShortcutsDialog: React.FC<ShortcutsDialogProps> = ({
  open,
  title,
  closeLabel,
  groups,
  emptyLabel,
  onClose,
  setting,
  mac,
  columns = 1,
  idPrefix = "shortcuts-dialog",
}) => {
  const dialog = useModalDialog(open, onClose);
  const visible = groups.filter((group) => group.rows.length > 0);
  const settingId = `${idPrefix}-setting`;
  const footer = setting ? (
    <Setting>
      <SettingText>
        <SettingLabel htmlFor={settingId}>{setting.label}</SettingLabel>
        {setting.description ? <SettingDescription id={`${settingId}-description`}>{setting.description}</SettingDescription> : null}
      </SettingText>
      <Switch
        id={settingId}
        value={setting.value}
        onChange={setting.onChange}
        size="xs"
        ariaLabel={setting.label}
        ariaDescribedBy={setting.description ? `${settingId}-description` : undefined}
      />
    </Setting>
  ) : undefined;
  return (
    <DialogShell
      open={open}
      size={columns === 2 ? "md" : "sm"}
      title={title}
      titleId={`${idPrefix}-title`}
      closeLabel={closeLabel}
      {...dialog}
      onClose={onClose}
      footerStart={footer}
    >
      {visible.length === 0 ? (
        emptyLabel ? <Empty>{emptyLabel}</Empty> : null
      ) : (
        <Sections $columns={columns}>
          {visible.map((group) => (
            <Section key={group.id} aria-labelledby={`${idPrefix}-group-${group.id}`}>
              <SectionTitle id={`${idPrefix}-group-${group.id}`}>{group.label}</SectionTitle>
              <Rows>
                {group.rows.map((row) => (
                  <Row key={row.id}>
                    <RowLabel $muted={row.soon}>{row.label}</RowLabel>
                    <RowKeys>
                      {row.soon ? <SoonBadge /> : null}
                      <ShortcutKeys chords={row.chords} mac={mac} merged={row.merged} />
                      {row.thenChords?.length ? (
                        <>
                          <PairDot aria-hidden="true">·</PairDot>
                          <ShortcutKeys chords={row.thenChords} mac={mac} />
                        </>
                      ) : null}
                    </RowKeys>
                  </Row>
                ))}
              </Rows>
            </Section>
          ))}
        </Sections>
      )}
    </DialogShell>
  );
};

export default ShortcutsDialog;
